/**
 * CreateCalendarHeader – month/year label + date strip for the create flow.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { addDays, startOfDay } from 'date-fns';
import { CalendarStrip } from '../../date';
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext';
import { CREATE_FIXTURE_WINDOW_DAYS } from '../../../src/create/constants/create.constants';
import { getCreateCalendarHeaderStyles } from './CreateCalendarHeader.styles';
import { useTheme } from '../../../src/theme/useTheme';
import { countPicksByDate } from '../../../src/create/helpers/pickIndicators.helper';

export const CreateCalendarHeader: React.FC = () => {
  const theme = useTheme();
  const styles = useMemo(() => getCreateCalendarHeaderStyles(theme), [theme]);
  const { selectedDate, setSelectedDate, pickSlips, games } = useCreatePage();
  // Pick count per date (specs/challenge-flow-redesign AC-3.9).
  const pickCountsByDate = useMemo(() => countPicksByDate(pickSlips, games), [pickSlips, games]);

  const todayStart = useMemo(() => startOfDay(new Date()), []);
  const maxAllowed = useMemo(
    () => addDays(todayStart, CREATE_FIXTURE_WINDOW_DAYS - 1),
    [todayStart]
  );

  useEffect(() => {
    const selectedStart = startOfDay(selectedDate);
    if (selectedStart < todayStart) {
      setSelectedDate(todayStart);
      return;
    }
    if (selectedStart > maxAllowed) {
      setSelectedDate(maxAllowed);
    }
  }, [selectedDate, setSelectedDate, todayStart, maxAllowed]);

  const handleDateSelect = useCallback(
    (date: Date) => {
      const dateStart = startOfDay(date);
      const clamped =
        dateStart < todayStart ? todayStart : dateStart > maxAllowed ? maxAllowed : dateStart;
      setSelectedDate(clamped);
    },
    [setSelectedDate, todayStart, maxAllowed]
  );

  return (
    <View style={styles.container} testID="create-calendar-header">
      <CalendarStrip
        selectedDate={selectedDate}
        onDateSelect={handleDateSelect}
        showMonthHeader={false}
        minDate={todayStart}
        maxDate={maxAllowed}
        dayBadgeCounts={pickCountsByDate}
        testID="create-date-strip"
      />
    </View>
  );
};
